import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  CUISINE_LABELS,
  CUISINES,
  MEAL_SLOTS,
  VIBE_LABELS,
  VIBES,
  type MealSlot,
  type PlannerDayDto,
  type PlannerPickDto,
  type PlannerVibe,
  type StoreDto,
} from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { SparkleIcon } from '../../components/icons';
import { Section } from '../../components/Section';
import { StorePicker } from '../../components/groceries/StorePicker';
import { AttendeePicker, chipButton } from '../../components/plans/AttendeePicker';
import { AutoplanCellDialog, type CellValue } from '../../components/plans/AutoplanCellDialog';
import { PlannerDayCard } from '../../components/plans/PlannerDayCard';
import { PlannerSwapSheet, type SwapReason } from '../../components/plans/PlannerSwapSheet';
import { groceryKeys } from '../groceries/queries';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  attendeeChoiceFor,
  attendeeSummary,
  buildWeekRequest,
  cellKey,
  dollarsInput,
  formatDollars,
  groupMealsByDate,
  guestNamesFor,
  loadAutoplanState,
  orderedSlots,
  parseDollars,
  saveAutoplanState,
  selectedDates,
  typicalServeAt,
  USUAL_ATTENDEES,
  usualServeAt,
  weekdayOf,
  weekSummary,
  type AttendeeChoice,
  type AutoplanState,
} from '../../lib/autoplan';
import { messageForError } from '../../lib/errors';
import {
  addDays,
  formatDayLong,
  formatDayShort,
  formatWeekLabel,
  isIsoDate,
  startOfWeek,
  todayIso,
} from '../../lib/planDates';
import {
  acceptableMeals,
  acceptBody,
  defaultPlanStart,
  formatClock,
  lockFor,
  mealKey,
  pinsExcept,
  withExcluded,
  type PlannerLock,
} from '../../lib/planner';
import { LEFTOVERS_LABEL, SLOT_LABEL } from '../../lib/plans';
import { scheduleKeys, WEEKDAY_LABEL } from '../../lib/schedule';

type Step = 1 | 2 | 3 | 4;
const STEPS: { step: Step; label: string }[] = [
  { step: 1, label: 'Who & when' },
  { step: 2, label: 'How' },
  { step: 3, label: 'Plan' },
  { step: 4, label: 'Add to plan' },
];
const mealPlacesKey = ['household', 'mealPlaces'] as const;

const VIBE_HINTS: Record<PlannerVibe, string> = {
  any: 'Whatever fits best',
  quick_easy: '30 minutes or less, few steps',
  one_pan: 'One pan or sheet pan, less washing up',
  batch_cook: 'Big batches and make-ahead',
  family_favorites: 'What everyone rated highly',
  comfort_classics: 'Casseroles and classics',
  fresh_light: 'Produce-heavy and light',
  fiber_forward: 'Beans, whole grains, veg',
  takeout_at_home: 'Your takeout favourites, homemade',
};

/**
 * Auto-plan wizard (D33): who eats when, how the week should feel, the
 * proposal with swaps/locks/skips, then "Add to plan". Answers persist per
 * household in this browser; nothing is saved on the server until the last
 * step. The manual calendar and "Plan my week" stay as they are.
 */
export function PlanAutoPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { auth } = useAuth();
  const householdId = auth.household?.id ?? 'none';
  const today = todayIso();

  const [state, setState] = useState<AutoplanState>(() =>
    loadAutoplanState(householdId, today, defaultPlanStart(today)),
  );
  const [step, setStep] = useState<Step>(1);
  const [exclude, setExclude] = useState<Record<string, string[]>>({});
  const [userLocks, setUserLocks] = useState<Record<string, PlannerLock>>({});
  const [pins, setPins] = useState<Record<string, PlannerLock>>({});
  const [skip, setSkip] = useState<string[]>([]);
  const [editingCell, setEditingCell] = useState<{ weekday: number; slot: MealSlot } | null>(null);
  const [editingSlot, setEditingSlot] = useState<MealSlot | null>(null);
  const [swapping, setSwapping] = useState<string | null>(null);
  const [budgetText, setBudgetText] = useState(() => dollarsInput(state.budgetCents));
  const [buildList, setBuildList] = useState(true);
  const [choosingStore, setChoosingStore] = useState(false);
  const [store, setStore] = useState<StoreDto | null>(null);

  useEffect(() => {
    saveAutoplanState(householdId, state);
  }, [householdId, state]);

  const people = useQuery({
    queryKey: queryKeys.people,
    queryFn: () => api.households.people.list(),
    staleTime: 60_000,
  });
  const places = useQuery({
    queryKey: mealPlacesKey,
    queryFn: () => api.households.mealPlaces.list(),
    staleTime: 60_000,
  });
  const mealTimes = useQuery({
    queryKey: scheduleKeys.mealTimes,
    queryFn: () => api.schedule.mealTimes(),
    staleTime: 60_000,
  });

  const personList = useMemo(() => people.data?.people ?? [], [people.data]);
  const placeList = useMemo(() => places.data?.places ?? [], [places.data]);
  const times = mealTimes.data?.mealTimes ?? [];
  const names = useMemo(() => new Map(personList.map((p) => [p.id, p.name])), [personList]);
  const placeIds = useMemo(
    () => (places.data ? new Set(placeList.map((p) => p.id)) : undefined),
    [places.data, placeList],
  );

  /** Wizard answers changed: the staged swaps/locks no longer apply. */
  function update(fn: (prev: AutoplanState) => AutoplanState) {
    setState(fn);
    setExclude({});
    setUserLocks({});
    setPins({});
    setSkip([]);
  }

  const slots = orderedSlots(state.slots);
  const dates = selectedDates(state);
  const canPlan = slots.length > 0 && dates.length > 0;

  const request = buildWeekRequest(
    state,
    { exclude, locked: { ...pins, ...userLocks }, skip },
    personList.map((p) => p.id),
    placeIds,
  );
  const proposal = useQuery({
    queryKey: ['planner', 'auto', request],
    queryFn: () => api.planner.week(request),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: step >= 3 && canPlan && !people.isPending && !places.isPending,
  });

  const days = useMemo(() => proposal.data?.days ?? [], [proposal.data]);
  const groups = groupMealsByDate(days);
  const ready = acceptableMeals(days);
  const summary = weekSummary(days, proposal.data?.cost?.budgetCents ?? state.budgetCents);
  const working = proposal.isFetching;
  const noRecipes = days.some(
    (d) => d.kind === 'cook' && d.skipped?.kind === 'no_recipe_fits' && d.availableMin === null,
  );
  const swapDay = swapping ? days.find((d) => mealKey(d) === swapping) : undefined;

  // ------------------------------------------------------------ helpers --

  /** What "usual" means for a slot (or one weekday of it). */
  function usualLabel(slot: MealSlot, weekday?: number): string {
    const rows = times.filter(
      (t) => t.slot === slot && (weekday === undefined || t.weekday === weekday),
    );
    const ids = new Set(rows.flatMap((r) => r.attendeePersonIds ?? []));
    if (ids.size === 0) return 'everyone';
    if (weekday === undefined && rows.some((r) => !r.attendeePersonIds?.length)) return 'routine';
    return [...ids].map((id) => names.get(id) ?? '?').join(', ');
  }

  function cellValue(weekday: number, slot: MealSlot): CellValue {
    return {
      attendees: state.cellAttendees[cellKey(weekday, slot)] ?? null,
      dineOut: state.dineOut[cellKey(weekday, slot)] ?? null,
      packedPlaceId: slot === 'lunch' ? (state.packed[String(weekday)] ?? null) : null,
    };
  }

  function saveCell(weekday: number, slot: MealSlot, v: CellValue) {
    const key = cellKey(weekday, slot);
    update((prev) => {
      const cellAttendees = { ...prev.cellAttendees };
      if (v.attendees) cellAttendees[key] = v.attendees;
      else delete cellAttendees[key];
      const dineOut = { ...prev.dineOut };
      if (v.dineOut) dineOut[key] = v.dineOut;
      else delete dineOut[key];
      const packed = { ...prev.packed };
      if (slot === 'lunch') {
        if (v.packedPlaceId && !v.dineOut) packed[String(weekday)] = v.packedPlaceId;
        else delete packed[String(weekday)];
      }
      return { ...prev, cellAttendees, dineOut, packed };
    });
    setEditingCell(null);
  }

  function cellText(weekday: number, slot: MealSlot): { top: string; bottom: string } {
    const key = cellKey(weekday, slot);
    const who = attendeeSummary(attendeeChoiceFor(state, weekday, slot), names, 'Usual');
    const out = state.dineOut[key];
    if (out) return { top: 'Out', bottom: out.note.trim() || who };
    const placeId = slot === 'lunch' ? state.packed[String(weekday)] : undefined;
    const place = placeId ? placeList.find((p) => p.id === placeId) : undefined;
    if (place) return { top: place.name, bottom: who };
    const t = usualServeAt(times, weekday, slot);
    return { top: t ? formatClock(t) : 'Home', bottom: who };
  }

  function changeFrom(next: string) {
    if (!isIsoDate(next)) return;
    update((prev) => ({ ...prev, from: next }));
  }

  // -------------------------------------------------- proposal actions --

  const feedback = useMutation({
    mutationFn: (input: { day: PlannerDayDto; recipeId: string }) =>
      api.planner.swapFeedback({
        date: input.day.date,
        slot: input.day.slot,
        recipeId: input.recipeId,
        reason: 'didnt_want',
        ...(input.day.attendees &&
        !input.day.attendees.everyone &&
        input.day.attendees.personIds.length
          ? { personIds: input.day.attendees.personIds }
          : {}),
      }),
  });

  function teach(day: PlannerDayDto, reason: SwapReason) {
    if (reason === 'didnt_want' && day.main) {
      feedback.mutate({ day, recipeId: day.main.recipe.recipeId });
    }
  }

  function chooseAlternative(day: PlannerDayDto, alt: PlannerPickDto, reason: SwapReason) {
    const key = mealKey(day);
    teach(day, reason);
    setPins(pinsExcept(days, key, mealKey));
    setUserLocks((prev) => ({
      ...prev,
      [key]: {
        mainRecipeId: alt.recipe.recipeId,
        sideRecipeIds: day.sides.map((s) => s.recipe.recipeId),
      },
    }));
    setSwapping(null);
  }

  function reroll(day: PlannerDayDto, reason: SwapReason) {
    if (!day.main) return;
    const key = mealKey(day);
    teach(day, reason);
    setPins(pinsExcept(days, key, mealKey));
    setExclude((prev) => withExcluded(prev, key, day.main!.recipe.recipeId));
    setSwapping(null);
  }

  function toggleLock(day: PlannerDayDto) {
    const key = mealKey(day);
    setUserLocks((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = lockFor(day);
      return next;
    });
  }

  function skipMeal(day: PlannerDayDto) {
    const key = mealKey(day);
    setSkip((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setUserLocks((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function restoreMeal(day: PlannerDayDto) {
    const key = mealKey(day);
    setSkip((prev) => prev.filter((k) => k !== key));
  }

  const accept = useMutation({
    mutationFn: () =>
      api.planner.accept(
        acceptBody(days, buildList ? { create: true, storeId: store?.id ?? null } : null, {
          guestNames: (d) => guestNamesFor(state, d),
        }),
      ),
    onSuccess: async (result) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.plans.all }),
        qc.invalidateQueries({ queryKey: ['planner'] }),
        qc.invalidateQueries({ queryKey: groceryKeys.all }),
      ]);
      const n = result.plans.length;
      const toast = `Added ${n} ${n === 1 ? 'meal' : 'meals'} to the plan.`;
      if (result.groceryListId) {
        await navigate(`/groceries/${result.groceryListId}`, { state: { toast } });
      } else {
        await navigate(`/plan?week=${startOfWeek(state.from)}`, { state: { toast } });
      }
    },
  });

  function mealTitle(d: PlannerDayDto): string {
    if (d.kind === 'dine_out') return d.dineOut?.note ? `Out: ${d.dineOut.note}` : 'Dining out';
    if (d.leftoversOf && !d.main) return `${LEFTOVERS_LABEL}: ${d.leftoversOf.title}`;
    const main = d.main?.recipe.title ?? '';
    return d.sides.length ? `${main} + ${d.sides.map((s) => s.recipe.title).join(', ')}` : main;
  }

  function whoText(d: PlannerDayDto): string {
    if (!d.attendees || d.attendees.everyone) return 'Everyone';
    const guests = guestNamesFor(state, d).slice(0, d.attendees.guests);
    while (guests.length < d.attendees.guests) guests.push(`Guest ${guests.length + 1}`);
    return [...d.attendees.personIds.map((id) => names.get(id) ?? '?'), ...guests].join(', ');
  }

  // ------------------------------------------------------------- render --

  const nextMonday = defaultPlanStart(addDays(today, 1));
  const n = ready.length;
  const budgetInvalid = budgetText.trim() !== '' && parseDollars(budgetText) === null;

  const stepNav = (
    <nav aria-label="Auto-plan steps">
      <ol className="grid grid-cols-4 gap-1 rounded-2xl border border-line bg-surface p-1">
        {STEPS.map(({ step: s, label }) => {
          const reachable = s <= 2 || (canPlan && (s === 3 || proposal.data !== undefined));
          const current = s === step;
          return (
            <li key={s}>
              <button
                type="button"
                className={`flex min-h-11 w-full flex-col items-center justify-center rounded-xl px-1 text-xs font-semibold transition-colors sm:flex-row sm:gap-1.5 sm:text-sm ${
                  current
                    ? 'bg-brand text-white shadow-sm'
                    : 'text-brand hover:bg-brand-soft disabled:text-muted disabled:hover:bg-transparent'
                }`}
                aria-current={current ? 'step' : undefined}
                disabled={!reachable}
                onClick={() => setStep(s)}
              >
                <span aria-hidden>{s}</span>
                <span className="truncate">{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );

  const footer = (
    back: Step | null,
    next: { to: Step; label: string; disabled?: boolean } | null,
  ) => (
    <div className="flex gap-2">
      {back ? (
        <Button type="button" variant="secondary" onClick={() => setStep(back)}>
          Back
        </Button>
      ) : null}
      {next ? (
        <Button type="button" disabled={next.disabled} onClick={() => setStep(next.to)}>
          {next.label}
        </Button>
      ) : null}
    </div>
  );

  return (
    <AppShell width="wide">
      <div className="flex flex-col gap-4 lg:gap-5">
        <div className="relative flex flex-col gap-1 overflow-hidden rounded-3xl border border-line bg-gradient-to-br from-brand-soft via-surface to-accent-soft p-5 sm:p-6">
          <span
            aria-hidden
            className="pointer-events-none absolute -top-10 -right-10 size-40 rounded-full bg-accent/30"
          />
          <Link
            to="/plan"
            className="relative inline-flex min-h-9 items-center self-start text-sm font-medium text-brand underline-offset-2 hover:underline"
          >
            ← Meal plan
          </Link>
          <h1 className="relative flex items-center gap-2.5 text-2xl sm:text-3xl">
            <span
              aria-hidden
              className="flex size-10 items-center justify-center rounded-xl bg-brand text-white"
            >
              <SparkleIcon className="size-5" />
            </span>
            Auto-plan
          </h1>
          <p className="relative max-w-2xl text-sm text-ink">
            Tell us who eats when and what kind of week it is; we&apos;ll propose every meal, and
            nothing is saved until you add it.
          </p>
        </div>

        {stepNav}

        {/* ------------------------------------------------ 1. Who & when */}
        {step === 1 ? (
          <Section title="Who & when" id="autoplan-who" icon={<span aria-hidden>1</span>}>
            <div className="flex flex-col gap-2">
              <Field
                id="autoplan-from"
                label="Week starting"
                type="date"
                value={state.from}
                min={today}
                onChange={(e) => changeFrom(e.target.value)}
              />
              <div className="flex flex-wrap gap-2" role="group" aria-label="Quick start dates">
                <button
                  type="button"
                  className={chipButton(state.from === today)}
                  aria-pressed={state.from === today}
                  onClick={() => changeFrom(today)}
                >
                  From today
                </button>
                {nextMonday !== today ? (
                  <button
                    type="button"
                    className={chipButton(state.from === nextMonday)}
                    aria-pressed={state.from === nextMonday}
                    onClick={() => changeFrom(nextMonday)}
                  >
                    Next week
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-medium text-ink" id="autoplan-days-label">
                Days
              </p>
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-labelledby="autoplan-days-label"
              >
                {WEEKDAY_LABEL.map((label, wd) => {
                  const on = state.weekdays.includes(wd);
                  return (
                    <button
                      key={label}
                      type="button"
                      className={`${chipButton(on)} min-w-11 px-2.5`}
                      aria-pressed={on}
                      aria-label={label}
                      onClick={() =>
                        update((prev) => ({
                          ...prev,
                          weekdays: on
                            ? prev.weekdays.filter((x) => x !== wd)
                            : [...prev.weekdays, wd].sort(),
                        }))
                      }
                    >
                      {label.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-medium text-ink" id="autoplan-slots-label">
                Meals
              </p>
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-labelledby="autoplan-slots-label"
              >
                {MEAL_SLOTS.map((slot) => {
                  const on = state.slots.includes(slot);
                  const t = typicalServeAt(times, slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      className={chipButton(on)}
                      aria-pressed={on}
                      onClick={() =>
                        update((prev) => ({
                          ...prev,
                          slots: on
                            ? prev.slots.filter((x) => x !== slot)
                            : orderedSlots([...prev.slots, slot]),
                        }))
                      }
                    >
                      {SLOT_LABEL[slot]}
                      {t ? (
                        <span className="font-normal opacity-80"> · {formatClock(t)}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {people.isError ? <Alert tone="error">{messageForError(people.error)}</Alert> : null}

            {slots.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <p className="text-sm font-medium text-ink">Who usually eats</p>
                <ul className="flex flex-wrap gap-1.5">
                  {slots.map((slot) => (
                    <li key={slot}>
                      <button
                        type="button"
                        className={chipButton(false)}
                        onClick={() => setEditingSlot(slot)}
                      >
                        {SLOT_LABEL[slot]}:{' '}
                        <span className="font-normal">
                          {attendeeSummary(
                            state.slotAttendees[slot] ?? USUAL_ATTENDEES,
                            names,
                            usualLabel(slot) === 'everyone' ? 'Everyone' : 'Usual',
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {canPlan ? (
              <div className="flex flex-col gap-1.5">
                <p className="text-sm font-medium text-ink">
                  Tap a meal to change who eats, eat out, or pack a lunch
                </p>
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `3.25rem repeat(${slots.length}, minmax(0, 1fr))`,
                  }}
                  role="table"
                  aria-label="Meals this week"
                >
                  <div role="row" className="contents">
                    <span role="columnheader" className="sr-only">
                      Day
                    </span>
                    {slots.map((slot) => (
                      <span
                        key={slot}
                        role="columnheader"
                        className="truncate px-1 text-center text-xs font-semibold text-muted"
                      >
                        {SLOT_LABEL[slot]}
                      </span>
                    ))}
                  </div>
                  {dates.map((date) => {
                    const wd = weekdayOf(date);
                    return (
                      <div role="row" className="contents" key={date}>
                        <span
                          role="rowheader"
                          className="flex items-center text-xs font-semibold text-ink"
                        >
                          {formatDayShort(date)}
                        </span>
                        {slots.map((slot) => {
                          const { top, bottom } = cellText(wd, slot);
                          const key = cellKey(wd, slot);
                          const special =
                            Boolean(state.dineOut[key]) ||
                            Boolean(state.cellAttendees[key]) ||
                            (slot === 'lunch' && Boolean(state.packed[String(wd)]));
                          return (
                            <div role="cell" key={slot} className="min-w-0">
                              <button
                                type="button"
                                className={`flex min-h-12 w-full min-w-0 flex-col items-center justify-center rounded-xl border px-1 py-1 text-center transition-colors ${
                                  special
                                    ? 'border-brand/50 bg-brand-soft'
                                    : 'border-line bg-surface hover:bg-brand-soft'
                                }`}
                                aria-label={`${formatDayLong(date)} ${SLOT_LABEL[slot]}: ${top}, ${bottom}`}
                                onClick={() => setEditingCell({ weekday: wd, slot })}
                              >
                                <span className="w-full truncate text-xs font-semibold text-brand">
                                  {top}
                                </span>
                                <span className="w-full truncate text-[11px] text-muted">
                                  {bottom}
                                </span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Alert tone="info">Pick at least one day and one meal to plan.</Alert>
            )}

            {footer(null, { to: 2, label: 'Next: how', disabled: !canPlan })}
          </Section>
        ) : null}

        {/* ------------------------------------------------------- 2. How */}
        {step === 2 ? (
          <Section title="How" id="autoplan-how" icon={<span aria-hidden>2</span>}>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-ink">The week&apos;s vibe</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {(['any', ...VIBES] as PlannerVibe[]).map((v) => {
                  const on = state.vibe === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={on}
                      onClick={() => update((prev) => ({ ...prev, vibe: v }))}
                      className={`flex min-h-16 flex-col items-start justify-center rounded-2xl border p-3 text-left transition-colors ${
                        on
                          ? 'border-brand bg-brand text-white shadow-sm'
                          : 'border-line bg-surface hover:bg-brand-soft'
                      }`}
                    >
                      <span className={`text-sm font-semibold ${on ? '' : 'text-brand'}`}>
                        {v === 'any' ? 'Any' : VIBE_LABELS[v]}
                      </span>
                      <span className={`text-xs ${on ? 'text-white/85' : 'text-muted'}`}>
                        {VIBE_HINTS[v]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-ink">
                Cuisines <span className="font-normal text-muted">(none = any)</span>
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {CUISINES.map((c) => {
                  const on = state.cuisines.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      className={chipButton(on)}
                      aria-pressed={on}
                      onClick={() =>
                        update((prev) => ({
                          ...prev,
                          cuisines: on
                            ? prev.cuisines.filter((x) => x !== c)
                            : [...prev.cuisines, c],
                        }))
                      }
                    >
                      {CUISINE_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="max-w-xs">
              <Field
                id="autoplan-budget"
                label="Weekly budget (optional)"
                inputMode="decimal"
                placeholder="150"
                value={budgetText}
                onChange={(e) => {
                  const text = e.target.value;
                  setBudgetText(text);
                  const cents = parseDollars(text);
                  if (text.trim() === '' || cents !== null) {
                    update((prev) => ({ ...prev, budgetCents: text.trim() === '' ? null : cents }));
                  }
                }}
                error={budgetInvalid ? 'Enter an amount like 150 or 149.50' : undefined}
              />
            </div>

            <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
              <input
                type="checkbox"
                checked={state.includeSides}
                onChange={(e) => update((prev) => ({ ...prev, includeSides: e.target.checked }))}
                className="size-5 accent-brand"
              />
              Add a side dish to lunches and dinners at home
            </label>

            {footer(1, { to: 3, label: 'Plan my meals', disabled: !canPlan || budgetInvalid })}
          </Section>
        ) : null}

        {/* ------------------------------------------------------ 3. Plan */}
        {step === 3 ? (
          <>
            {proposal.data ? (
              <section
                aria-label="Week summary"
                aria-live="polite"
                className={`flex flex-wrap items-center gap-x-4 gap-y-1 card p-3 sm:p-4 ${
                  summary.overBudget ? 'border-orange/60' : ''
                }`}
              >
                <p className="text-sm text-muted">
                  {formatWeekLabel(proposal.data.from, proposal.data.to)}
                  {working ? ' · planning…' : ''}
                </p>
                <p className="text-sm font-semibold text-ink">
                  {summary.meals} {summary.meals === 1 ? 'meal' : 'meals'} · ≈{' '}
                  {formatDollars(summary.totalCents)}
                  {summary.budgetCents !== null ? (
                    <span className={summary.overBudget ? 'text-orange-ink' : 'text-muted'}>
                      {' '}
                      of {formatDollars(summary.budgetCents)}
                      {summary.overBudget ? ' — over budget' : ''}
                    </span>
                  ) : null}
                </p>
                {summary.missingPrices > 0 ? (
                  <p className="text-xs text-muted">
                    {summary.missingPrices} {summary.missingPrices === 1 ? 'price' : 'prices'}{' '}
                    estimated
                  </p>
                ) : null}
                {summary.warnings > 0 ? (
                  <p className="text-xs font-semibold text-orange-ink">
                    {summary.warnings} {summary.warnings === 1 ? 'warning' : 'warnings'}
                  </p>
                ) : null}
              </section>
            ) : null}

            {proposal.isPending ? (
              <ul
                className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
                aria-busy="true"
                aria-label="Planning your week"
              >
                {Array.from({ length: 6 }, (_, i) => (
                  <li key={i} className="skeleton h-56 rounded-2xl" />
                ))}
              </ul>
            ) : null}

            {proposal.isError ? (
              <Alert tone="error">
                {messageForError(proposal.error)}{' '}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={() => void proposal.refetch()}
                >
                  Try again
                </button>
              </Alert>
            ) : null}

            {noRecipes ? (
              <Alert tone="info">
                There are no recipes to plan with yet.{' '}
                <Link to="/recipes/new" className="font-semibold text-brand underline">
                  Add a recipe
                </Link>{' '}
                or rate a few shared ones, and the planner will fill your week.
              </Alert>
            ) : null}

            {proposal.data ? (
              <ol
                className={`flex flex-col gap-5 transition-opacity ${working ? 'opacity-70' : ''}`}
              >
                {groups.map((g) => (
                  <li key={g.date} className="flex flex-col gap-2">
                    <h2 className="text-lg font-semibold">{formatDayLong(g.date)}</h2>
                    <ol className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {g.meals.map((d) => (
                        <PlannerDayCard
                          key={mealKey(d)}
                          day={d}
                          timezone={proposal.data.timezone}
                          userLocked={Boolean(userLocks[mealKey(d)])}
                          busy={working || accept.isPending}
                          onSwap={() => setSwapping(mealKey(d))}
                          onToggleLock={() => toggleLock(d)}
                          onSkip={() => skipMeal(d)}
                          onRestore={() => restoreMeal(d)}
                          detail={{
                            personName: (id) => names.get(id) ?? 'Someone',
                            guestNames: guestNamesFor(state, d),
                            slotHeading: true,
                          }}
                        />
                      ))}
                    </ol>
                  </li>
                ))}
              </ol>
            ) : null}

            {footer(2, {
              to: 4,
              label: n === 0 ? 'Nothing to add' : `Next: add ${n} ${n === 1 ? 'meal' : 'meals'}`,
              disabled: n === 0 || working,
            })}
          </>
        ) : null}

        {/* ------------------------------------------------ 4. Add to plan */}
        {step === 4 ? (
          <Section title="Add to plan" id="autoplan-add" icon={<span aria-hidden>4</span>}>
            {ready.length === 0 ? (
              <p className="text-sm text-muted">Nothing to add — go back and plan some meals.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line" aria-label="Meals to add">
                {ready.map((d) => (
                  <li key={mealKey(d)} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-muted">
                        {formatDayShort(d.date)} · {SLOT_LABEL[d.slot]}
                        {d.kind === 'packed' && d.place ? ` · packed for ${d.place.name}` : ''}
                      </p>
                      <p className="leading-snug font-semibold">{mealTitle(d)}</p>
                      <p className="text-xs text-muted">{whoText(d)}</p>
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-ink">
                      {d.cost ? `≈ ${formatDollars(d.cost.totalCents)}` : '—'}
                    </p>
                  </li>
                ))}
                <li className="flex items-center justify-between gap-3 py-2">
                  <p className="font-semibold">Week total</p>
                  <p
                    className={`text-sm font-semibold ${summary.overBudget ? 'text-orange-ink' : 'text-ink'}`}
                  >
                    ≈ {formatDollars(summary.totalCents)}
                    {summary.budgetCents !== null
                      ? ` of ${formatDollars(summary.budgetCents)}`
                      : ''}
                  </p>
                </li>
              </ul>
            )}

            <div className="flex flex-col gap-1">
              <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  checked={buildList}
                  onChange={(e) => setBuildList(e.target.checked)}
                  className="size-5 accent-brand"
                />
                Also build the grocery list
              </label>
              {buildList ? (
                <button
                  type="button"
                  className="min-h-9 self-start pl-7 text-sm font-semibold text-brand underline-offset-2 hover:underline"
                  onClick={() => setChoosingStore((v) => !v)}
                  aria-expanded={choosingStore}
                >
                  {store ? `Store: ${store.chain} ${store.name}` : 'Choose a store (optional)'}
                </button>
              ) : null}
              {buildList && choosingStore ? (
                <StorePicker
                  selectedId={store?.id ?? null}
                  onPick={(picked) => {
                    setStore(picked);
                    setChoosingStore(false);
                  }}
                />
              ) : null}
              <p className="text-xs text-muted">
                Thaw reminders are added for anything that needs it.
              </p>
            </div>

            {accept.isError ? <Alert tone="error">{messageForError(accept.error)}</Alert> : null}

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button
                type="button"
                disabled={n === 0 || working || accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending
                  ? 'Adding…'
                  : n === 0
                    ? 'Nothing to add'
                    : `Add ${n} ${n === 1 ? 'meal' : 'meals'} to plan`}
              </Button>
            </div>
          </Section>
        ) : null}
      </div>

      {editingCell ? (
        <AutoplanCellDialog
          key={cellKey(editingCell.weekday, editingCell.slot)}
          open
          weekday={editingCell.weekday}
          slot={editingCell.slot}
          value={cellValue(editingCell.weekday, editingCell.slot)}
          slotDefault={state.slotAttendees[editingCell.slot] ?? USUAL_ATTENDEES}
          usualLabel={usualLabel(editingCell.slot, editingCell.weekday)}
          serveAt={usualServeAt(times, editingCell.weekday, editingCell.slot)}
          people={personList}
          places={placeList}
          onClose={() => setEditingCell(null)}
          onSave={(v) => saveCell(editingCell.weekday, editingCell.slot, v)}
        />
      ) : null}

      {editingSlot ? (
        <SlotAttendeesDialog
          key={editingSlot}
          slot={editingSlot}
          value={state.slotAttendees[editingSlot] ?? USUAL_ATTENDEES}
          usualLabel={usualLabel(editingSlot)}
          people={personList}
          onClose={() => setEditingSlot(null)}
          onSave={(choice) => {
            const slot = editingSlot;
            update((prev) => {
              const slotAttendees = { ...prev.slotAttendees };
              if (choice.personIds === null && choice.guests.length === 0)
                delete slotAttendees[slot];
              else slotAttendees[slot] = choice;
              return { ...prev, slotAttendees };
            });
            setEditingSlot(null);
          }}
        />
      ) : null}

      {swapDay ? (
        <PlannerSwapSheet
          key={swapping}
          open
          day={swapDay}
          onClose={() => setSwapping(null)}
          onChoose={(alt, reason) => chooseAlternative(swapDay, alt, reason)}
          onReroll={(reason) => reroll(swapDay, reason)}
        />
      ) : null}
    </AppShell>
  );
}

function SlotAttendeesDialog({
  slot,
  value,
  usualLabel,
  people,
  onClose,
  onSave,
}: {
  slot: MealSlot;
  value: AttendeeChoice;
  usualLabel: string;
  people: Parameters<typeof AttendeePicker>[0]['people'];
  onClose: () => void;
  onSave: (choice: AttendeeChoice) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <Dialog open title={`Who eats ${SLOT_LABEL[slot].toLowerCase()}`} onClose={onClose}>
      <p className="-mt-2 text-sm text-muted">
        For every {SLOT_LABEL[slot].toLowerCase()} this week; change single days in the grid.
      </p>
      <AttendeePicker
        idPrefix={`slot-${slot}`}
        choice={draft}
        people={people}
        usualLabel={usualLabel}
        onChange={setDraft}
      />
      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={() => onSave(draft)}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}
